import { tools } from "@/config/tools";
import { Locale, SITE_URL, withLocale } from "@/lib/i18n";
import { fillSeoText, getSeoCopy } from "@/lib/seo-copy";
import { Tool } from "@/types";

export function getFAQs(tool: Tool, locale: Locale) {
  const copy = getSeoCopy(locale);

  return [
    {
      question: fillSeoText(copy.questionHow, tool.name),
      answer: fillSeoText(copy.answerHow, tool.name),
    },
    {
      question: fillSeoText(copy.questionFree, tool.name),
      answer: fillSeoText(copy.answerFree, tool.name),
    },
    {
      question: fillSeoText(copy.questionSafe, tool.name),
      answer: fillSeoText(copy.answerSafe, tool.name),
    },
    {
      question: fillSeoText(copy.questionFormats, tool.name),
      answer: getFileFormatAnswer(tool, locale),
    },
    {
      question: fillSeoText(copy.questionInstall, tool.name),
      answer: fillSeoText(copy.answerInstall, tool.name),
    },
  ];
}

export function generateFAQSchema(tool: Tool, locale: Locale) {
  const faqs = getFAQs(tool, locale);

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function getToolOverview(tool: Tool, locale: Locale) {
  const copy = getSeoCopy(locale);

  if (tool.type === "pdf") {
    return fillSeoText(copy.overviewPdf, tool.name);
  }

  if (tool.type === "image") {
    return fillSeoText(copy.overviewImage, tool.name);
  }

  if (tool.type === "text") {
    return fillSeoText(copy.overviewText, tool.name);
  }

  return fillSeoText(copy.overviewOther, tool.name);
}

export function getToolUseCases(tool: Tool, locale: Locale) {
  const copy = getSeoCopy(locale);

  if (tool.type === "pdf") {
    return copy.useCasesPdf;
  }

  if (tool.type === "image") {
    return copy.useCasesImage;
  }

  if (tool.type === "text") {
    return copy.useCasesText;
  }

  return copy.useCasesOther;
}

export function getRelatedTools(tool: Tool, limit = 4) {
  return tools
    .filter((candidate) => candidate.id !== tool.id && candidate.type === tool.type)
    .slice(0, limit);
}

function getFileFormatAnswer(tool: Tool, locale: Locale): string {
  const copy = getSeoCopy(locale);

  if (tool.type === "pdf") {
    return fillSeoText(copy.pdfFormats, tool.name);
  }

  if (tool.type === "image") {
    return fillSeoText(copy.imageFormats, tool.name);
  }

  if (tool.type === "text") {
    return fillSeoText(copy.textFormats, tool.name);
  }

  return fillSeoText(copy.otherFormats, tool.name);
}

export function generateToolSchema(tool: Tool, locale: Locale) {
  const copy = getSeoCopy(locale);

  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: tool.name,
    description: tool.description,
    applicationSubCategory: `${tool.category} ${copy.toolWord}`.trim(),
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    url: `${SITE_URL}${withLocale(locale, `/${tool.slug}`)}`,
    provider: {
      "@type": "Organization",
      name: "Dogufy",
      url: SITE_URL,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    featureList: copy.featureList,
  };
}
